// Text-shaped renderers: sanitised HTML, GFM markdown, the xlsx download
// frame, and the raw-payload fallback.

import DOMPurify from "dompurify";
import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";

import type { Artifact } from "@/types/eryndal";
import { artifactToMarkdown } from "@/utils/artifactMarkdown";
import { ArtifactFrame, CopyButton } from "./common";
import { downloadBlob, safeFilename } from "./download";

export function HtmlArtifact({
  artifact,
  kindLabel,
}: {
  artifact: Artifact;
  kindLabel: string;
}) {
  const sanitised = useMemo(() => DOMPurify.sanitize(artifact.payload), [artifact.payload]);
  const filename = `${safeFilename(artifact.title, kindLabel)}.md`;
  return (
    <ArtifactFrame
      title={artifact.title}
      kindLabel={kindLabel}
      // This renderer had no download at all, which is where the model's HTML
      // tables land — so a table rendered here could not be exported except by
      // copying raw markup out of "show as text". Export through the markdown
      // serializer so a table leaves as a real GFM table.
      rightSlot={
        <button
          type="button"
          onClick={() =>
            downloadBlob(artifactToMarkdown(artifact), filename, "text/markdown")
          }
          className="shrink-0 font-mono text-[0.7rem] text-oxblood-500 transition-colors duration-200 ease-atelier hover:text-oxblood-600"
        >
          download .md ↓
        </button>
      }
    >
      <div
        className="prose-eryndal max-w-none"
        dangerouslySetInnerHTML={{ __html: sanitised }}
      />
    </ArtifactFrame>
  );
}

export function MarkdownArtifact({ artifact }: { artifact: Artifact }) {
  const filename = `${safeFilename(artifact.title, "note")}.md`;
  return (
    <ArtifactFrame
      title={artifact.title}
      kindLabel="markdown"
      rightSlot={
        <span className="flex items-center gap-3">
          <CopyButton getText={() => artifact.payload} />
          <button
            type="button"
            // Through the serializer so a `table` artifact that reached this
            // renderer as GFM pipes still exports with its heading attached.
            onClick={() =>
              downloadBlob(artifactToMarkdown(artifact), filename, "text/markdown")
            }
            className="font-mono text-[0.7rem] tracking-normal normal-case text-oxblood-500 hover:text-oxblood-600"
          >
            download .md ↓
          </button>
        </span>
      }
    >
      <div className="prose-eryndal max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
          {artifact.payload}
        </ReactMarkdown>
      </div>
    </ArtifactFrame>
  );
}

export function XlsxArtifact({ artifact }: { artifact: Artifact }) {
  const filename = `${safeFilename(artifact.title, "workbook")}.xlsx`;
  return (
    <ArtifactFrame
      title={artifact.title}
      kindLabel="xlsx"
      rightSlot={
        <button
          type="button"
          onClick={() => {
            try {
              // Backend may emit base64-encoded binary in payload.
              const bin = atob(artifact.payload);
              const bytes = new Uint8Array(bin.length);
              for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
              const blob = new Blob([bytes], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              });
              downloadBlob(blob, filename, blob.type);
            } catch {
              const dl = (artifact.metadata?.download_url as string | undefined) ?? "";
              if (dl) window.open(dl, "_blank", "noreferrer");
            }
          }}
          className="font-mono text-[0.7rem] tracking-normal normal-case text-oxblood-500 hover:text-oxblood-600"
        >
          download .xlsx ↓
        </button>
      }
    >
      <p className="font-serif italic text-warmgray-400">
        Spreadsheet generated. Use the download link to open in Excel or Numbers.
      </p>
    </ArtifactFrame>
  );
}

/**
 * Last-resort view: the artifact's payload as plain text.
 *
 * Artifacts now live only in the drawer, so a renderer that throws would
 * otherwise strand the content with no way to reach it. The payload has always
 * arrived intact in those cases — it is the browser-side renderer that failed
 * (a malformed mermaid graph, an unparseable table) — so falling back to the
 * raw text keeps the content reachable in the same turn, with no round trip.
 */
export function RawArtifact({ artifact, reason }: { artifact: Artifact; reason?: string | null }) {
  return (
    <ArtifactFrame
      title={artifact.title}
      kindLabel={`${artifact.kind} · raw`}
      rightSlot={
        <span className="shrink-0 font-mono text-[0.66rem] text-oxblood-400">
          {reason ? "render failed" : "as text"}
        </span>
      }
    >
      {reason ? (
        <p className="mb-3 rounded-panel border border-oxblood-300 bg-oxblood-50 px-3 py-2 font-serif text-xs text-oxblood-500">
          This artifact could not be rendered, so its content is shown as text.{" "}
          <span className="font-mono text-[0.7rem] text-warmgray-500">{reason}</span>
        </p>
      ) : null}
      <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-espresso-700">
        {artifact.payload}
      </pre>
    </ArtifactFrame>
  );
}
